<script setup>
  import { computed, onMounted, ref, nextTick, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Eraser, Trash } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";
  import { Badge } from "@/components/ui/badge";
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
  import { LightListContainer } from "@/components/episto-ui/list";
  import { LightListItem } from "@/components/episto-ui/list";
  import { useMAudienceStore } from "@stores/mAudience/mAudience";

  const isInterestsOpen = ref(false);
  const interestOptionsLoading = ref(false);
  const hasSelectionChanged = ref(false);

  const mAudienceStore = useMAudienceStore();
  const {
    removeAllInterests,
    removeInterest,
    updateInterestOptions,
    updateMAudienceInterests,
    fetchInterestsSuggestions,
  } = mAudienceStore;
  const { getMAudience, getMAudienceInterests, getMAudienceInterestIds, getInterestOptions, getSuggestionOptions } =
    storeToRefs(mAudienceStore);

  const toSelectOption = (option) => ({
    value: option.value,
    label: `${option.label}${option.type ? ` (${option.type.replaceAll("_", " ")})` : ""}`,
  });

  const interestSelectOptions = computed(() => (getInterestOptions.value || []).map(toSelectOption));
  const suggestionSelectOptions = computed(() => (getSuggestionOptions.value || []).map(toSelectOption));

  const interestCountLabel = computed(() => {
    const count = getMAudienceInterests.value?.length || 0;
    return count > 1 ? `${count} interests` : `${count} interest`;
  });

  const handleInterestInput = async (value) => {
    interestOptionsLoading.value = true;
    const isLatestSearch = await updateInterestOptions(value);
    if (isLatestSearch) interestOptionsLoading.value = false;
  };

  const handleInterestsChange = (ids) => {
    isInterestsOpen.value = true;
    hasSelectionChanged.value = true;
    updateMAudienceInterests(ids);
  };

  const refreshSuggestionsOnClose = async (open) => {
    if (open || !hasSelectionChanged.value) return;
    hasSelectionChanged.value = false;
    await fetchInterestsSuggestions();
  };

  const handleRemoveInterest = async (index) => {
    removeInterest(index);
    await fetchInterestsSuggestions();
  };

  const handleRemoveAllInterests = async () => {
    removeAllInterests();
    await fetchInterestsSuggestions();
  };

  const getInterestLabel = (interest) => interest.name;
  const getInterestType = (interest) => interest.type?.replaceAll("_", " ");

  watch(
    () => getMAudience.value.token,
    async () => {
      isInterestsOpen.value = !!getMAudienceInterests.value?.length;
      await fetchInterestsSuggestions();
    },
  );

  onMounted(() => {
    nextTick(async () => {
      if (getMAudienceInterests.value?.length) isInterestsOpen.value = true;
      await fetchInterestsSuggestions();
    });
  });
</script>

<template>
  <h4 class="heading-section mt-4 mb-3">Interests</h4>
  <div class="flex gap-2">
    <div class="w-3/4">
      <Select
        remote
        filterable
        multiple
        hide-tags
        select-all
        placeholder="Search interests..."
        :model-value="getMAudienceInterestIds"
        :options="interestSelectOptions"
        :loading="interestOptionsLoading"
        @search="handleInterestInput"
        @update:model-value="handleInterestsChange"
        @open-change="refreshSuggestionsOnClose" />
    </div>
    <div class="w-1/4">
      <Select
        multiple
        hide-tags
        select-all
        content-class="min-w-72"
        placeholder="Suggestions"
        :model-value="getMAudienceInterestIds"
        :options="suggestionSelectOptions"
        @update:model-value="handleInterestsChange"
        @open-change="refreshSuggestionsOnClose" />
    </div>
  </div>
  <Collapsible
    v-if="getMAudienceInterests?.length"
    :open="isInterestsOpen"
    @update:open="isInterestsOpen = $event">
    <CollapsibleTrigger class="flex w-full items-center justify-between py-2 text-sm font-medium">
      Selected interests
    </CollapsibleTrigger>
    <CollapsibleContent>
      <div class="flex items-center justify-between mb-2">
        <Badge variant="secondary">
          {{ interestCountLabel }} selected
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          @click="handleRemoveAllInterests">
          <Eraser class="h-4 w-4 mr-1" />
          <span>Remove all interests</span>
        </Button>
      </div>
      <LightListContainer>
        <LightListItem
          v-for="(interest, index) in getMAudienceInterests"
          :key="interest.key"
          :title="getInterestLabel(interest)"
          :subtitle="getInterestType(interest)">
          <template #actions>
            <Button
              variant="ghost"
              size="icon-sm"
              @click="handleRemoveInterest(index)">
              <Trash class="h-4 w-4" />
            </Button>
          </template>
        </LightListItem>
      </LightListContainer>
    </CollapsibleContent>
  </Collapsible>
</template>
